import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Users, 
  CheckCircle, 
  Clock,
  TrendingUp,
  Calendar,
  RefreshCw,
  Phone,
} from 'lucide-react';
import { StatsCard, HealthStatus } from '../../components/dashboard';
import { Card, Loading, Badge } from '../../components/common';
import useAuth from '../../hooks/useAuth';
import conversationsService from '../../services/api/conversations.service';
import tenantsService from '../../services/api/tenants.service';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const formatSafeDate = (dateString) => {
  try {
    if (!dateString) return 'Data não disponível';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Data inválida';
    return formatDistanceToNow(date, {
      addSuffix: true,
      locale: ptBR,
    });
  } catch (error) {
    return 'Data inválida';
  }
};

// 🔥 Função para detectar status real baseado no context
const getConversationStatus = (conversation) => {
  let currentStep = '';
  
  if (conversation.context) {
    currentStep = conversation.context.current_step || 
                 conversation.context.currentStep || 
                 conversation.context.step ||
                 conversation.context.stepId ||
                 conversation.context.step_id || '';
  }
  
  if (!currentStep && conversation) {
    currentStep = conversation.current_step ||
                 conversation.currentStep ||
                 conversation.step || '';
  }
  
  const stepStr = String(currentStep).toLowerCase();
  
  if (stepStr.includes('perguntar_reinicio')) {
    return 'closed';
  }
  
  if (conversation.context?.flow_finished === true || 
      conversation.context?.flow_finished === 'true') {
    return 'closed';
  }
  
  if (conversation.context?.restarted_by_user === true || 
      conversation.context?.restarted_by_user === 'true') {
    return 'open';
  }
  
  return conversation.status || 'open';
};

const TenantOverview = () => {
  const { user } = useAuth();
  const tenantId = user?.tenantId || user?.id;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [recentConversations, setRecentConversations] = useState([]);
  const [stats, setStats] = useState({
    totalConversations: 0,
    activeConversations: 0,
    resolvedConversations: 0,
    resolvedPercentage: 0,
  });

  useEffect(() => {
    if (tenantId) {
      loadData();
    }
  }, [tenantId]);

  const loadData = async () => {
    try {
      setLoading(true);

      console.log('📊 Carregando dados do tenant:', tenantId);

      try {
        const tenant = await tenantsService.getById(tenantId);
        setTenantInfo(tenant);
      } catch (error) {
        console.error('⚠️ Erro ao carregar tenant:', error);
      }

      // 🔥 MUDANÇA: Busca TODAS as conversas e filtra localmente
      const allData = await conversationsService.getConversations(tenantId, {
        status: 'all',
        limit: 100,
      });

      const allConversations = allData.items || allData.conversations || [];
      
      // 🔥 Filtra usando nossa função que detecta o context
      const openList = allConversations.filter(conv => getConversationStatus(conv) === 'open');
      const closedList = allConversations.filter(conv => getConversationStatus(conv) === 'closed');
      
      const total = allConversations.length;
      const resolvedPercentage = total > 0 ? Math.round((closedList.length / total) * 100) : 0;

      setStats({
        totalConversations: total,
        activeConversations: openList.length,
        resolvedConversations: closedList.length,
        resolvedPercentage,
      });

      setRecentConversations(openList.slice(0, 5));

      console.log('✅ Stats calculados:', {
        total,
        open: openList.length,
        closed: closedList.length,
        percentage: resolvedPercentage,
      });
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loading size="lg" text="Carregando dashboard..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-primary-400 via-primary-500 to-primary-600 rounded-2xl p-8 text-white shadow-glow">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2 font-display">
              {getGreeting()}, {user?.name?.split(' ')[0] || tenantInfo?.name || 'Usuário'}!
            </h1>
            <p className="text-primary-50 text-lg">
              Aqui está um resumo das suas automações
            </p>
          </div>
          <div className="hidden lg:flex items-center gap-4">
            <div className="flex items-center gap-2 text-white/90">
              <Calendar className="w-5 h-5" />
              <span className="text-sm font-medium">
                {new Date().toLocaleDateString('pt-BR', { 
                  weekday: 'long', 
                  day: 'numeric', 
                  month: 'long' 
                })}
              </span>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <p className="text-white/80 text-sm mb-1">Total de Conversas</p>
            <p className="text-2xl font-bold">{stats.totalConversations}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <p className="text-white/80 text-sm mb-1">Taxa de Resolução</p>
            <p className="text-2xl font-bold">{stats.resolvedPercentage}%</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <p className="text-white/80 text-sm mb-1">Abertas</p>
            <p className="text-2xl font-bold">{stats.activeConversations}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <p className="text-white/80 text-sm mb-1">Resolvidas</p>
            <p className="text-2xl font-bold">{stats.resolvedConversations}</p>
          </div>
        </div>
      </div>

      {tenantInfo && (
        <Card>
          <Card.Body>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{tenantInfo.name}</h3>
                <p className="text-sm text-gray-600">ID: {tenantInfo.id}</p>
              </div>
              <div className="flex gap-2">
                <Badge variant={tenantInfo.isActive ? 'success' : 'danger'} dot>
                  {tenantInfo.isActive ? 'Ativo' : 'Inativo'}
                </Badge>
                <Badge variant={tenantInfo.botPaused ? 'warning' : 'primary'}>
                  {tenantInfo.botPaused ? 'Bot Pausado' : 'Bot Ativo'}
                </Badge>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total de Conversas"
          value={stats.totalConversations}
          icon={MessageSquare}
          subtitle="todas as conversas"
          color="primary"
        />
        
        <StatsCard
          title="Conversas Ativas"
          value={stats.activeConversations}
          icon={Clock}
          subtitle="em andamento"
          color="warning"
        />
        
        <StatsCard
          title="Resolvidas"
          value={stats.resolvedConversations}
          icon={CheckCircle}
          subtitle="fechadas"
          color="success"
        />
        
        <StatsCard
          title="Taxa de Resolução"
          value={`${stats.resolvedPercentage}%`}
          icon={TrendingUp}
          subtitle="conversas resolvidas"
          color="info"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <Card.Header>
            <Card.Title>Conversas por Status</Card.Title>
            <Card.Description>Distribuição das conversas atuais</Card.Description>
          </Card.Header>
          <Card.Body>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Resolvidas</span>
                  <span className="text-sm font-semibold text-gray-900">{stats.resolvedPercentage}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-green-400 to-green-500 transition-all duration-500" 
                    style={{ width: `${stats.resolvedPercentage}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Em Andamento</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {stats.totalConversations > 0 
                      ? Math.round((stats.activeConversations / stats.totalConversations) * 100)
                      : 0}%
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-yellow-400 to-yellow-500 transition-all duration-500" 
                    style={{ 
                      width: `${stats.totalConversations > 0 
                        ? (stats.activeConversations / stats.totalConversations) * 100 
                        : 0}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <Card.Title>Resumo Geral</Card.Title>
            <Card.Description>Estatísticas principais</Card.Description>
          </Card.Header>
          <Card.Body>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Total de Conversas</p>
                    <p className="text-xs text-gray-500">Todas registradas</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{stats.totalConversations}</p>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Resolvidas</p>
                    <p className="text-xs text-gray-500">Fechadas com sucesso</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{stats.resolvedConversations}</p>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Abertas</p>
                    <p className="text-xs text-gray-500">Aguardando atendimento</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{stats.activeConversations}</p>
              </div>
            </div>
          </Card.Body>
        </Card>

        <HealthStatus />
      </div>

      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <div>
              <Card.Title>Conversas Recentes</Card.Title>
              <Card.Description>Últimas {recentConversations.length} conversas abertas</Card.Description>
            </div>
            <TrendingUp className="w-5 h-5 text-primary-500" />
          </div>
        </Card.Header>
        <Card.Body>
          {recentConversations.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p>Nenhuma conversa ativa no momento</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentConversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {conversation.profile_name || conversation.context?.nome || 'Sem nome'}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Phone className="w-3 h-3" />
                        <span>{conversation.wa_id || conversation.lead?.phone}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {conversation.bot_paused && (
                      <Badge variant="warning" size="sm">
                        Bot Pausado
                      </Badge>
                    )}
                    <p className="text-xs text-gray-500">
                      {formatSafeDate(conversation.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default TenantOverview;